import { ChevronLeft, ChevronRight, Clock, Package, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMySubPacks } from '@/api/member';
import { fmtUsd } from '@/lib/money';

/**
 * SubPackBadgeCarousel — live countdown badge for each of the caller's
 * active Sub Packs. Shows ONE badge at a time with optional left/right
 * navigation when the user has 2+ packs. Sorted most-recently-purchased
 * first (index 0).
 *
 * - Polls /api/me/subpacks every 30s so newly purchased packs appear without
 *   requiring a page reload.
 * - Local clock ticks every 1s (zero backend hit) to drive the countdown.
 * - Hides itself entirely when the user has 0 active packs.
 */

const TIER_INTERVAL_LABEL = (hours) => {
  if (!hours || hours <= 0) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}min`;
  return `${hours}h`;
};

const formatCountdown = (msRemaining) => {
  if (msRemaining <= 0) {
    return { h: '00', m: '00', s: '00', overdue: true };
  }
  const total = Math.floor(msRemaining / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return {
    h: String(h).padStart(2, '0'),
    m: String(m).padStart(2, '0'),
    s: String(s).padStart(2, '0'),
    overdue: false,
  };
};

const SubPackBadgeCarousel = () => {
  const [packs, setPacks] = useState([]);
  const [index, setIndex] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const indexRef = useRef(0);
  indexRef.current = index;

  // Fetch packs from backend; poll every 30s.
  const fetchPacks = useCallback(async () => {
    try {
      const data = await getMySubPacks();
      const list = Array.isArray(data?.packs) ? data.packs : [];
      setPacks(list);
      // Keep current index in range if list shrunk.
      if (indexRef.current >= list.length) {
        setIndex(Math.max(0, list.length - 1));
      }
    } catch {
      // Network/auth hiccup — keep existing state, retry next interval.
    }
  }, []);

  useEffect(() => {
    fetchPacks();
    const id = setInterval(fetchPacks, 60000);
    return () => clearInterval(id);
  }, [fetchPacks]);

  // Local 1-second tick for the countdown — does NOT hit the backend.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const active = packs[index];
  const msRemaining = useMemo(() => {
    if (!active?.next_release_at) return 0;
    const t = Date.parse(active.next_release_at);
    return Number.isFinite(t) ? t - now : 0;
  }, [active, now]);

  // Bail early when there's nothing to show — widget stays invisible to keep
  // the dashboard clean for newly-signed-up users.
  if (!active || packs.length === 0) return null;

  const cd = formatCountdown(msRemaining);
  const progressPct = active.total_positions > 0 ? Math.min(100, (active.positions_released / active.total_positions) * 100) : 0;
  const canPrev = index > 0;
  const canNext = index < packs.length - 1;
  const showCarousel = packs.length > 1;

  return (
    <section className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="subpack-badge-carousel">
      {/* corner glow */}
      <div
        className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,194,14,0.18) 0%, transparent 70%)' }}
      />

      {/* header row: title + arrows */}
      <div className="relative z-10 flex items-center justify-between mb-4">
        <button
          type="button"
          disabled={!canPrev}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          className={`w-9 h-9 rounded-xl flex items-center justify-center transition ${
            canPrev
              ? 'bg-[rgba(255,182,18,0.10)] border border-[rgba(255,182,18,0.42)] text-[#FFC20E] hover:bg-[rgba(255,182,18,0.18)]'
              : 'bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.10)] text-[var(--sc-text-mute)] opacity-40 cursor-not-allowed'
          } ${showCarousel ? '' : 'invisible'}`}
          aria-label="Previous Sub Pack"
          data-testid="subpack-carousel-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2 text-[#FFC20E]">
            <Sparkles className="w-3.5 h-3.5" />
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold">Active Sub Pack</div>
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          {showCarousel ? (
            <div className="text-[10px] text-[var(--sc-text-mute)] mt-1 tabular-nums">
              {index + 1} of {packs.length}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          disabled={!canNext}
          onClick={() => setIndex((i) => Math.min(packs.length - 1, i + 1))}
          className={`w-9 h-9 rounded-xl flex items-center justify-center transition ${
            canNext
              ? 'bg-[rgba(255,182,18,0.10)] border border-[rgba(255,182,18,0.42)] text-[#FFC20E] hover:bg-[rgba(255,182,18,0.18)]'
              : 'bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.10)] text-[var(--sc-text-mute)] opacity-40 cursor-not-allowed'
          } ${showCarousel ? '' : 'invisible'}`}
          aria-label="Next Sub Pack"
          data-testid="subpack-carousel-next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* badge body */}
      <div className="relative z-10 flex flex-col items-center text-center" data-testid="subpack-badge-body">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-[#FFC20E]" />
          <div className="font-display font-bold text-2xl sc-gold-text" data-testid="subpack-badge-tier">
            {fmtUsd(active.tier_price_usd)} Sub Pack
          </div>
        </div>

        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(255,182,18,0.10)] border border-[rgba(255,182,18,0.42)]">
          <span className="font-display font-bold text-[15px] text-[#FFC20E] tabular-nums" data-testid="subpack-badge-progress">
            {active.positions_released} / {active.total_positions}
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">positions placed</span>
        </div>

        {/* progress bar */}
        <div
          className="w-full max-w-xs mt-4 h-2 rounded-full overflow-hidden bg-[rgba(255,255,255,0.06)] border border-[rgba(255,182,18,0.20)]"
          aria-hidden="true"
        >
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${progressPct}%`,
              background: 'linear-gradient(90deg, #FFB612 0%, #FFC20E 100%)',
            }}
          />
        </div>

        {/* countdown */}
        <div className="mt-5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-[var(--sc-text-mute)]">
          <Clock className="w-3 h-3" /> Next Drop
        </div>
        <div
          className="font-display font-bold text-3xl md:text-4xl mt-1.5 sc-tabular"
          style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.06em' }}
          data-testid="subpack-badge-countdown"
        >
          {cd.overdue ? (
            <span className="text-[#FFC20E] text-xl md:text-2xl uppercase tracking-[0.2em]">Releasing…</span>
          ) : (
            <>
              <span>{cd.h}</span>
              <span className="text-[var(--sc-text-mute)] mx-1">:</span>
              <span>{cd.m}</span>
              <span className="text-[var(--sc-text-mute)] mx-1">:</span>
              <span>{cd.s}</span>
            </>
          )}
        </div>
        {!cd.overdue ? (
          <div className="flex gap-3 text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mt-1">
            <span>hr</span>
            <span>min</span>
            <span>sec</span>
          </div>
        ) : null}

        <div className="mt-4 text-[11px] text-[var(--sc-text-dim)]">Drops every {TIER_INTERVAL_LABEL(active.release_interval_hours)}</div>

        {/* dots indicator */}
        {showCarousel ? (
          <div className="mt-4 flex items-center gap-1.5" role="tablist" aria-label="Sub Pack carousel position" data-testid="subpack-carousel-dots">
            {packs.map((p, i) => (
              <button
                key={p.pack_id || i}
                type="button"
                role="tab"
                aria-selected={i === index}
                onClick={() => setIndex(i)}
                className={`rounded-full transition ${
                  i === index ? 'w-3 h-2 bg-[#FFC20E]' : 'w-2 h-2 bg-[rgba(255,255,255,0.20)] hover:bg-[rgba(255,182,18,0.45)]'
                }`}
                aria-label={`Show pack ${i + 1}`}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SubPackBadgeCarousel;
